import { styled } from '@mui/material/styles';
import { Box, Paper, Typography } from '@mui/material';
import { BRAND_COLORS, alpha } from '../../styles/theme';

export const GuidePageWrapper = styled(Box)({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.05),
});

export const GuideBody = styled(Box)({
  display: 'flex',
  flex: 1,
  maxWidth: 1400,
  margin: '0 auto',
  width: '100%',
  padding: '24px 24px 48px',
  gap: 24,
});

export const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 280,
  flexShrink: 0,
  borderRight: `1px solid ${alpha(BRAND_COLORS.forestGreen.main, 0.1)}`,
  paddingRight: 16,
  paddingTop: 100,
  position: 'sticky',
  top: 24,
  alignSelf: 'flex-start',
  maxHeight: 'calc(100vh - 48px)',
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

export const SidebarItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  padding: '8px 12px',
  cursor: 'pointer',
  borderLeft: active ? `3px solid ${BRAND_COLORS.yellow.main}` : '3px solid transparent',
  backgroundColor: active ? alpha(BRAND_COLORS.yellow.main, 0.08) : 'transparent',
  color: active ? BRAND_COLORS.forestGreen.main : alpha(BRAND_COLORS.forestGreen.main, 0.7),
  fontWeight: active ? 600 : 400,
  fontSize: '0.875rem',
  borderRadius: '0 4px 4px 0',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.05),
    color: BRAND_COLORS.forestGreen.main,
  },
}));

export const SidebarSubItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  padding: '6px 12px 6px 28px',
  cursor: 'pointer',
  borderLeft: active ? `3px solid ${BRAND_COLORS.yellow.main}` : '3px solid transparent',
  backgroundColor: active ? alpha(BRAND_COLORS.yellow.main, 0.05) : 'transparent',
  color: active ? BRAND_COLORS.forestGreen.main : alpha(BRAND_COLORS.forestGreen.main, 0.55),
  fontWeight: active ? 500 : 400,
  fontSize: '0.8125rem',
  borderRadius: '0 4px 4px 0',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.04),
    color: BRAND_COLORS.forestGreen.main,
  },
}));

export const ContentArea = styled(Box)({
  flex: 1,
  minWidth: 0,
});

// Title row: icon+title on left, back button on right
export const ContentTitle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 32,
  paddingBottom: 16,
  borderBottom: `3px solid ${BRAND_COLORS.yellow.main}`,
});

// White card wrapping each workflow section
export const SectionCard = styled(Paper)({
  padding: '28px 32px',
  marginBottom: 24,
  borderRadius: 12,
  border: `1px solid ${alpha(BRAND_COLORS.forestGreen.main, 0.08)}`,
  boxShadow: '0 2px 8px rgba(0, 61, 45, 0.06)',
  scrollMarginTop: 24,
});

export const SectionTitle = styled(Typography)({
  fontWeight: 600,
  color: BRAND_COLORS.forestGreen.main,
  marginBottom: 16,
});

// Subsection with subtle background to distinguish from parent section text
export const SubsectionWrapper = styled(Box)({
  backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.02),
  borderLeft: `3px solid ${alpha(BRAND_COLORS.forestGreen.main, 0.1)}`,
  borderRadius: '0 8px 8px 0',
  padding: '16px 20px',
  marginTop: 20,
  marginBottom: 16,
  scrollMarginTop: 24,
});

export const SubsectionTitle = styled(Typography)({
  fontWeight: 600,
  color: BRAND_COLORS.forestGreen.main,
  marginBottom: 12,
});

export const StepList = styled('ol')({
  paddingLeft: 24,
  margin: '12px 0',
  counterReset: 'step-counter',
  listStyle: 'none',
  '& > li': {
    counterIncrement: 'step-counter',
    position: 'relative',
    paddingLeft: 12,
    marginBottom: 10,
    '&::before': {
      content: 'counter(step-counter)',
      position: 'absolute',
      left: -24,
      top: 2,
      width: 22,
      height: 22,
      borderRadius: '50%',
      backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
      color: BRAND_COLORS.forestGreen.main,
      fontSize: '0.75rem',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
});

export const StepItem = styled('li')({
  fontSize: '0.9375rem',
  lineHeight: 1.6,
  color: '#333',
});

export const TipBox = styled(Box)({
  backgroundColor: alpha(BRAND_COLORS.yellow.main, 0.1),
  borderLeft: `4px solid ${BRAND_COLORS.yellow.main}`,
  padding: '12px 16px',
  borderRadius: '0 8px 8px 0',
  marginTop: 16,
  marginBottom: 16,
});

export const WarningBox = styled(Box)({
  backgroundColor: 'rgba(244, 67, 54, 0.06)',
  borderLeft: '4px solid #f44336',
  padding: '12px 16px',
  borderRadius: '0 8px 8px 0',
  marginTop: 16,
  marginBottom: 16,
});
